import React, { useEffect, useRef, useState } from "react";
import client, { apiErr } from "../../lib/api";
import { Panel } from "../ui-kit";
import { Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Field, Empty, inputCls, money, fmtDate } from "../team/shared";
import { ManualBrokerage } from "./ManualBrokerage";

export function CommissionUpload({ onDone, members = [] }) {
  const [reports, setReports] = useState([]);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [cols, setCols] = useState({ mt5_col: "", amount_col: "" });
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const [mode, setMode] = useState("manual");
  const [adv, setAdv] = useState(false);
  const fileRef = useRef();
  const load = () => client.get("/partners/commission-reports").then(({ data }) => setReports(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("CSV file choose karein"); return; }
    const fd = new FormData();
    fd.append("file", file); fd.append("period", period);
    if (cols.mt5_col) fd.append("mt5_col", cols.mt5_col);
    if (cols.amount_col) fd.append("amount_col", cols.amount_col);
    setBusy(true);
    try {
      const { data } = await client.post("/partners/commission-upload", fd);
      setLast(data); toast.success(`${data.matched} rows matched`); fileRef.current.value = ""; load(); onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete report ${r.filename}? Isse linked earnings bhi hat jayengi.`)) return;
    try { await client.delete(`/partners/commission-reports/${r.id}`); load(); onDone(); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Panel title="Commission Reports" testid="commission-panel">
      <div className="p-4 pb-0 space-y-3">
        <Field label="Period (month)"><input data-testid="commission-period" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className={inputCls} /></Field>
        <div className="grid grid-cols-2 border border-[color:var(--border-strong)]">
          {[["manual", "Manual Entry (MT5 ID)"], ["csv", "CSV Upload"]].map(([k, l]) => (
            <button key={k} data-testid={`commission-mode-${k}`} onClick={() => setMode(k)} className={`py-2 text-xs transition-colors duration-200 ${mode === k ? "bg-gold text-black font-semibold" : "text-slate-400 hover:text-white"}`}>{l}</button>
          ))}
        </div>
      </div>
      {mode === "manual" ? <ManualBrokerage members={members} period={period} onSaved={() => { load(); onDone(); }} /> : (
      <div className="p-4 space-y-3 border-b border-[color:var(--border-subtle)]">
        <p className="text-xs text-slate-400">Broker ki commission CSV upload karein. Har row ka MT5 ID partner ke client se match hoga aur partner ka % share uski earnings me add hoga.</p>
        <Field label="CSV file"><input data-testid="commission-file" ref={fileRef} type="file" accept=".csv,text/csv" className="text-xs text-slate-300 pt-2" /></Field>
        <button type="button" data-testid="commission-adv-toggle" onClick={() => setAdv(!adv)} className="text-[11px] text-slate-500 hover:text-gold">{adv ? "− Hide" : "+ Advanced:"} custom CSV column names (sirf tab jab auto-detect fail ho)</button>
        {adv && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="CSV header name for MT5"><input data-testid="commission-mt5-col" value={cols.mt5_col} onChange={(e) => setCols({ ...cols, mt5_col: e.target.value })} className={inputCls} placeholder="column name, e.g. Login" /></Field>
          <Field label="CSV header name for amount"><input data-testid="commission-amount-col" value={cols.amount_col} onChange={(e) => setCols({ ...cols, amount_col: e.target.value })} className={inputCls} placeholder="column name, e.g. Rebate" /></Field>
        </div>
        )}
        <button data-testid="commission-upload-btn" disabled={busy} onClick={upload} className="btn-gold px-4 py-2 text-xs flex items-center gap-1.5 disabled:opacity-60"><Upload size={14} />{busy ? "Uploading…" : "Upload & Map"}</button>
        {last && (
          <div data-testid="commission-result" className="text-xs border border-gold/40 bg-gold/5 p-3 space-y-1">
            <div className="text-slate-200">Columns used: <span className="font-mono text-gold">{last.mt5_col}</span> / <span className="font-mono text-gold">{last.amount_col}</span></div>
            <div className="text-slate-300">{last.matched} matched · {last.unmatched_count} unmatched · Gross {money(last.gross_total)} · Team payout {money(last.team_payout)}</div>
            {last.unmatched_count > 0 && <div className="text-slate-500">Unmatched MT5: {last.unmatched.slice(0, 15).map((u) => u.mt5_id).join(", ")}{last.unmatched_count > 15 ? "…" : ""}</div>}
          </div>
        )}
      </div>
      )}
      {reports.length === 0 ? <Empty text="No reports uploaded yet." /> : reports.map((r) => (
        <div key={r.id} data-testid={`report-${r.id}`} className="flex flex-wrap items-center gap-3 px-4 py-2.5 border-b border-[color:var(--border-subtle)] last:border-0 text-sm">
          <span className="text-white">{r.period}</span>
          <span className="text-xs text-slate-500 truncate max-w-[180px]">{r.filename}</span>
          <span className="text-xs text-slate-400">{r.manual ? "Manual" : `${r.matched}/${r.rows} matched`}</span>
          <span className="ml-auto font-mono text-gold">{money(r.team_payout)}</span>
          <span className="text-[11px] text-slate-500">{fmtDate(r.created_at)}</span>
          <button data-testid={`delete-report-${r.id}`} onClick={() => del(r)} className="text-slate-500 hover:text-bear"><Trash2 size={14} /></button>
        </div>
      ))}
    </Panel>
  );
}
